// Page Tchat Équipe - Messagerie interne d'équipe
import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, Paperclip, Users, Smile,
  MoreVertical, Loader2, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TchatProPage() {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const currentUserId = localStorage.getItem('userId');

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchMessages();
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedTeam]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pro/teams`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTeams(data);
        if (data.length > 0) {
          setSelectedTeam(data[0]);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedTeam) return;
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/messages?limit=100`, { headers });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/pro/teams/${selectedTeam.id}/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content: newMessage })
      });
      if (res.ok) {
        setNewMessage('');
        fetchMessages();
        inputRef.current?.focus();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  // Grouper les messages par date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.created_at).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#E8650A]" />
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Aucune équipe</h3>
          <p className="text-gray-500">Crée d'abord une équipe pour discuter avec tes collaborateurs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-[#1E3A5F]">{selectedTeam?.name || 'Tchat équipe'}</h2>
            <p className="text-sm text-gray-500">{selectedTeam?.member_count || 0} membres</p>
          </div>
        </div>
        
        {/* Sélecteur d'équipe */}
        {teams.length > 1 && (
          <select
            value={selectedTeam?.id || ''}
            onChange={(e) => {
              const team = teams.find(t => t.id === e.target.value);
              setSelectedTeam(team);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
          >
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-gray-50">
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date}>
            {/* Séparateur de date */}
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white px-3 py-1 rounded-full text-xs text-gray-500 border border-gray-200 capitalize">
                {date}
              </div>
            </div>

            {/* Messages du jour */}
            <div className="space-y-3">
              {dateMessages.map((message, idx) => {
                const isMine = message.is_mine;
                const showAvatar = idx === 0 || dateMessages[idx - 1]?.author_id !== message.author_id;
                
                return (
                  <div 
                    key={message.id}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-2 max-w-[80%] ${isMine ? 'flex-row-reverse' : ''}`}>
                      {/* Avatar */}
                      {!isMine && showAvatar ? (
                        <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex-shrink-0 flex items-center justify-center text-white text-xs font-medium">
                          {message.author?.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      ) : !isMine ? (
                        <div className="w-8" /> // Placeholder pour alignement
                      ) : null}

                      {/* Bulle de message */}
                      <div>
                        {!isMine && showAvatar && (
                          <p className="text-xs text-gray-500 mb-1 ml-1">{message.author?.name}</p>
                        )}
                        <div 
                          className={`px-4 py-2 rounded-2xl ${
                            isMine 
                              ? 'bg-[#E8650A] text-white rounded-br-md' 
                              : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                        <p className={`text-[10px] text-gray-400 mt-1 ${isMine ? 'text-right mr-1' : 'ml-1'}`}>
                          {new Date(message.created_at).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {messages.length === 0 && (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Aucun message</p>
            <p className="text-gray-400 text-sm">Commence la conversation avec ton équipe !</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="bg-white border-t border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Écris ton message..."
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-full focus:ring-2 focus:ring-[#E8650A] focus:border-transparent pr-12"
            />
          </div>
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-12 h-12 bg-[#E8650A] text-white rounded-full flex items-center justify-center hover:bg-[#d55a09] transition-colors disabled:opacity-50"
          >
            {sending ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
